package com.rosario.juridico.domain

import com.rosario.juridico.marketplace.model.Abogado
import com.rosario.juridico.marketplace.viewmodel.InitState
import com.rosario.juridico.platform.FirebaseProxy
import com.rosario.juridico.marketplace.billing.BillingProvider
import dev.gitlive.firebase.Firebase
import dev.gitlive.firebase.firestore.firestore
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.flow.*
import kotlinx.coroutines.launch

class AuthUseCase private constructor() {
    private val scope = CoroutineScope(Dispatchers.Main)

    private val _isLoggedIn = MutableStateFlow(FirebaseProxy.isLoggedIn())
    val isLoggedIn: StateFlow<Boolean> = _isLoggedIn.asStateFlow()

    private val _isEmailVerified = MutableStateFlow(FirebaseProxy.isEmailVerified())
    val isEmailVerified: StateFlow<Boolean> = _isEmailVerified.asStateFlow()

    private val _currentUserAbogado = MutableStateFlow<Abogado?>(null)
    val currentUserAbogado: StateFlow<Abogado?> = _currentUserAbogado.asStateFlow()

    private val _appInitState = MutableStateFlow<InitState>(InitState.Loading)
    val appInitState: StateFlow<InitState> = _appInitState.asStateFlow()

    private val _isAuthInitializing = MutableStateFlow(true)
    val isAuthInitializing: StateFlow<Boolean> = _isAuthInitializing.asStateFlow()

    private val _billingSubscribed = MutableStateFlow(false)

    // Un único flujo que combina todos los orígenes de premium
    val isUsuarioAbogadoSuscrito: StateFlow<Boolean> = combine(
        AppUsageManager.instance.userStatus,
        _billingSubscribed,
        _currentUserAbogado
    ) { appStatus, billingSub, abogado ->
        appStatus.isPremium || billingSub || (abogado?.isSubscribed == true)
    }.stateIn(scope, SharingStarted.Eagerly, false)

    init {
        scope.launch {
            FirebaseProxy.observeAuthState().collectLatest { uid ->
                val loggedIn = !uid.isNullOrBlank() && !FirebaseProxy.isAnonymous()
                _isLoggedIn.value = loggedIn
                _isEmailVerified.value = FirebaseProxy.isEmailVerified()

                if (!loggedIn || uid.isNullOrBlank()) {
                    _currentUserAbogado.value = null
                    _isAuthInitializing.value = false
                    _appInitState.value = InitState.Unauthenticated
                } else {
                    _appInitState.value = InitState.Loading
                    try {
                        val db = Firebase.firestore
                        val doc = db.collection("abogados").document(uid).get()

                        if (doc.exists) {
                            val mat = try { doc.get<String?>("matricula") } catch (_: Exception) { null }
                            val tel = try { doc.get<String?>("telefono") } catch (_: Exception) { null }
                            val nom = try { doc.get<String?>("nombre") } catch (_: Exception) { null }
                            val ape = try { doc.get<String?>("apellido") } catch (_: Exception) { null }
                            val email = try { doc.get<String?>("email") } catch (_: Exception) { null }
                            val sub = try { doc.get<Boolean?>("isSubscribed") } catch (_: Exception) { false }

                            val finalMat = mat.orEmpty()
                            val finalTel = tel.orEmpty()

                            val abogado = Abogado(
                                id = uid,
                                nombre = nom.orEmpty().ifBlank { "Abogado" },
                                apellido = ape.orEmpty(),
                                email = email.orEmpty(),
                                telefono = finalTel,
                                matricula = finalMat,
                                isSubscribed = sub ?: false
                            )

                            _currentUserAbogado.value = abogado

                            if (finalMat.isNotBlank()) {
                                _appInitState.value = InitState.AuthenticatedAndComplete
                            } else {
                                val fallbackAbogado = loadCurrentUserAbogadoSuspend(uid)
                                if (fallbackAbogado?.matricula?.isNotBlank() == true) {
                                    _appInitState.value = InitState.AuthenticatedAndComplete
                                } else {
                                    _appInitState.value = InitState.NeedsOnboarding
                                }
                            }
                        } else {
                            val fallbackAbogado = loadCurrentUserAbogadoSuspend(uid)
                            if (fallbackAbogado?.matricula?.isNotBlank() == true) {
                                _appInitState.value = InitState.AuthenticatedAndComplete
                            } else {
                                _currentUserAbogado.value = null
                                _appInitState.value = InitState.NeedsOnboarding
                            }
                        }
                    } catch (e: Exception) {
                        println("Error consultando 'abogados/$uid': ${e.message}")
                        val fallbackAbogado = loadCurrentUserAbogadoSuspend(uid)
                        if (fallbackAbogado?.matricula?.isNotBlank() == true) {
                            _appInitState.value = InitState.AuthenticatedAndComplete
                        } else {
                            _appInitState.value = InitState.NeedsOnboarding
                        }
                    } finally {
                        _isAuthInitializing.value = false
                    }
                }
            }
        }
    }

    private suspend fun loadCurrentUserAbogadoSuspend(uid: String): Abogado? {
        return try {
            val abogado = FirebaseProxy.getAbogado(uid)
            val repo = PerfilAbogadoRepository()
            val perfilResult = repo.obtenerPerfil(uid)
            val perfil = (perfilResult as? PerfilAbogadoResult.Success)?.data

            val finalMat = perfil?.matricula.orEmpty()
            val resultAbogado = abogado?.copy(matricula = finalMat) ?: Abogado(id = uid, matricula = finalMat)
            _currentUserAbogado.value = resultAbogado
            resultAbogado
        } catch (e: Exception) {
            null
        }
    }

    fun attachBillingProvider(billingProvider: BillingProvider) {
        scope.launch {
            billingProvider.isSubscribed.collectLatest { subscribed ->
                _billingSubscribed.value = subscribed
                updateSubscriptionInFirestore(subscribed)
            }
        }
    }

    private suspend fun updateSubscriptionInFirestore(subscribed: Boolean) {
        val uid = FirebaseProxy.getCurrentUserId() ?: _currentUserAbogado.value?.id ?: return
        try {
            FirebaseProxy.updateSubscription(uid, subscribed)
            _currentUserAbogado.value = _currentUserAbogado.value?.copy(isSubscribed = subscribed)
        } catch (e: Exception) {}
    }

    fun refreshAuthState() {
        val loggedIn = FirebaseProxy.isLoggedIn()
        val verified = FirebaseProxy.isEmailVerified()
        _isLoggedIn.value = loggedIn
        _isEmailVerified.value = verified
        
        if (loggedIn) {
            val uid = FirebaseProxy.getCurrentUserId()
            if (uid != null) {
                scope.launch {
                    loadCurrentUserAbogadoSuspend(uid)
                }
            }
        } else {
            _currentUserAbogado.value = null
        }
    }

    fun reloadUser() {
        val uid = FirebaseProxy.getCurrentUserId()
        if (uid != null) {
            scope.launch {
                loadCurrentUserAbogadoSuspend(uid)
            }
        }
    }

    fun changeProfessional() {
        _currentUserAbogado.value = null
        _appInitState.value = InitState.NeedsOnboarding
    }

    fun clearSession() {
        _currentUserAbogado.value = null
        _isLoggedIn.value = false
        _isEmailVerified.value = false
        _appInitState.value = InitState.Unauthenticated
    }

    fun updateLocalAbogado(abogado: Abogado) {
        _currentUserAbogado.value = abogado
        if (!abogado.matricula.isNullOrBlank()) {
            _appInitState.value = InitState.AuthenticatedAndComplete
        }
    }

    companion object {
        val instance by lazy { AuthUseCase() }
    }
}